<template>
  <div class="heatmap-panel">
    <div class="heatmap-header">
      <h3 class="heatmap-title">MAPA DE CALOR DE PRECIOS</h3>
      <span class="heatmap-meta">{{ heatmapData.length }} productos · {{ competidores.length }} competidores</span>
    </div>
    <div class="table-container">
      <table class="heatmap-table">
        <thead>
          <tr>
            <th class="th-product th-sticky">Producto</th>
            <th
              v-for="competidor in competidores"
              :key="competidor"
              class="th-comp"
              :class="{ 'th-sticky-vip': isVisaovip(competidor) }"
            >
              {{ formatStoreName(competidor) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="producto in heatmapData" :key="producto.codigo">
            <td class="td-product td-sticky">
              <span class="product-code">[{{ producto.codigo }}]</span>
              <span class="product-desc">{{ producto.descripcion }}</span>
            </td>
            <td
              v-for="competidor in competidores"
              :key="competidor"
              class="td-price"
              :class="[
                { 'td-sticky-vip': isVisaovip(competidor) },
                getCellChangeClass(producto.codigo, competidor)
              ]"
              :style="getCellStyle(producto.precios[competidor], producto.precios)"
              :title="getCellTooltip(producto, competidor)"
            >
              <span class="change-arrow" v-if="getCambio(producto.codigo, competidor)">
                <span v-if="getCambio(producto.codigo, competidor).tipo === 'up'">↑</span>
                <span v-else-if="getCambio(producto.codigo, competidor).tipo === 'down'">↓</span>
                <span v-else-if="getCambio(producto.codigo, competidor).tipo === 'appeared'">✦</span>
              </span>
              <span v-if="producto.precios[competidor] != null">{{ formatPrice(producto.precios[competidor]) }}</span>
              <span v-else class="td-empty"
                :class="{ 'td-disappeared': getCambio(producto.codigo, competidor)?.tipo === 'disappeared' }">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="heatmap-legend">
      <span class="legend-label">ESCALA:</span>
      <div class="legend-item"><span class="legend-box legend-min"></span><span>más barato</span></div>
      <div class="legend-item"><span class="legend-box legend-mid"></span><span>intermedio</span></div>
      <div class="legend-item"><span class="legend-box legend-max"></span><span>más caro</span></div>
      <div class="legend-item"><span class="legend-box legend-none"></span><span>sin dato</span></div>
      <div class="legend-sep"></div>
      <div class="legend-item"><span class="legend-change up">↑</span><span>subió</span></div>
      <div class="legend-item"><span class="legend-change down">↓</span><span>bajó</span></div>
      <div class="legend-item"><span class="legend-change appeared">✦</span><span>apareció</span></div>
      <div class="legend-item"><span class="legend-change disappeared">—</span><span>desapareció</span></div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';

const props = defineProps({
  heatmapData: { type: Array, default: () => [] },
  fechasPorCompetidor: { type: Object, default: () => ({}) },
  cambios: { type: Object, default: () => ({}) }
});

const competidores = computed(() => {
  if (!props.heatmapData || props.heatmapData.length === 0) return [];
  return Object.keys(props.heatmapData[0].precios);
});

const isVisaovip = (name) => name === 'visaovip' || name === 'visaovip_importados';

const STORE_ABBREV = {
  'visaovip_importados': 'Visaovip Imp', 'visaovip': 'Visaovip',
  'nissei_comprasparaguai': 'Nissei CP', 'top_dek_comprasparaguai': 'Top Dek CP',
  'atacado_connect_comprasparaguai': 'Atacado CP', 'atacado_connect': 'Atacado',
  'shopping_china': 'Shop China', 'casa_americana': 'Casa Amer.',
  'charme_perfumaria': 'Charme', 'champs_elysees_perfumaria': 'Champs',
  'bonjour_perfumeria': 'Bonjour', 'best_shop_paraguai': 'Best Shop',
  'atacado_brasil': 'Atac. Brasil', 'atacado_vape': 'Atac. Vape',
  'dr_cell_atacado': 'Dr Cell', 'centro_de_motorola': 'Ctr Motorola',
  'aroma_store_sa': 'Aroma Store', 'victoria_store': 'Victoria',
  'atlantico_shop': 'Atlantico', 'pioneer_international': 'Pioneer Int.',
  'star_company': 'Star Co.', 'vape_paraguay': 'Vape PY',
  'top_house': 'Top House', 'mario_cell': 'Mario Cell',
  'smart_store': 'Smart Store', 'high_goods': 'High Goods',
  'mobile_zone': 'Mobile Zone', 'world_of_vape': 'World Vape',
  'super_games': 'Super Games', 'flash_importados': 'Flash Imp.',
  'epic_importados': 'Epic Imp.', 'matrix_importados': 'Matrix Imp.',
  'mega_eletronicos': 'Mega Elet.', 'mega_eletro': 'Mega Eletro',
  'prime_shop': 'Prime Shop', 'asm_group': 'ASM Group',
  'new_zone': 'New Zone', 'terra_nova': 'Terra Nova',
  'roma_shopping': 'Roma Shop', 'elegancia_company': 'Elegancia',
  'brands_for_less': 'Brands', 'madrid_center': 'Madrid',
  'macedonia': 'Macedonia', 'pontocom': 'Pontocom', 'agatres': 'Agatres',
  'genove': 'Genove', 'la_petisquera': 'La Petisquera',
  'multipass': 'Multipass', 'sampa_vapor': 'Sampa Vapor',
  'top_dek': 'Top Dek', 'nissei': 'Nissei', 'computech': 'Computech',
  'adeshop': 'Adeshop', 'mini_carlos': 'Mini Carlos', 'eshop': 'Eshop',
  'paradise': 'Paradise', 'tecnotienda': 'Tecnotienda', 'one_click': 'One Click',
  'tellecon_cell': 'Tellecon', 'pro_book': 'Pro Book', 'icompy': 'Icompy',
  'compras_hy': 'Compras HY', 'tche_loco': 'Tche Loco', 'digicell': 'Digicell',
  'flytec': 'Flytec', 'alborada': 'Alborada', 'cellshop': 'Cellshop',
};

const formatStoreName = (name) => {
  if (!name) return '';
  if (STORE_ABBREV[name]) return STORE_ABBREV[name];
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatPrice = (precio) => {
  if (precio === null || precio === undefined) return '';
  return parseFloat(precio.toFixed(2)).toString();
};

// ============ CAMBIOS ============
const getCambio = (codigo, competidor) => {
  return props.cambios[`${codigo}__${competidor}`] || null;
};

const getCellChangeClass = (codigo, competidor) => {
  const c = getCambio(codigo, competidor);
  if (!c) return '';
  return `cell-${c.tipo}`;
};

// ============ COLORES ============
const getCellStyle = (precio, todosLosPrecios) => {
  if (precio === null || precio === undefined) return {};
  const preciosValidos = Object.values(todosLosPrecios).filter(p => typeof p === 'number');
  if (preciosValidos.length < 2) {
    return { backgroundColor: 'rgba(113, 113, 122, 0.15)', color: 'var(--color-text)' };
  }
  const minPrecio = Math.min(...preciosValidos);
  const maxPrecio = Math.max(...preciosValidos);
  if (minPrecio === maxPrecio) {
    return { backgroundColor: 'rgba(251, 191, 36, 0.25)', color: 'var(--color-text)', fontWeight: '700' };
  }
  if (precio === minPrecio) return { backgroundColor: '#059669', color: '#ffffff', fontWeight: '700' };
  if (precio === maxPrecio) return { backgroundColor: '#b91c1c', color: '#ffffff', fontWeight: '700' };
  const midPrecio = minPrecio + (maxPrecio - minPrecio) / 2;
  let hue;
  if (precio < midPrecio) {
    hue = 140 - ((precio - minPrecio) / (midPrecio - minPrecio)) * 95;
  } else {
    hue = 45 - ((precio - midPrecio) / (maxPrecio - midPrecio)) * 45;
  }
  return { backgroundColor: `hsl(${hue}, 65%, 42%)`, color: '#ffffff', fontWeight: '600' };
};

const formatPYTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString + (isoString.endsWith('Z') ? '' : 'Z'));
  const opts = { timeZone: 'America/Asuncion', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
  return d.toLocaleString('es-PY', opts).replace(',', '');
};

const getCellTooltip = (producto, competidor) => {
  const fecha = props.fechasPorCompetidor?.[producto.codigo]?.[competidor];
  const precio = producto.precios[competidor];
  const tiendaName = formatStoreName(competidor);
  const cambio = getCambio(producto.codigo, competidor);
  let base = '';
  if (precio === null || precio === undefined) {
    base = `${tiendaName}: sin precio en los ultimos 7 dias`;
  } else if (fecha) {
    base = `${tiendaName}: $${precio} • actualizado ${formatPYTime(fecha)} PY`;
  } else {
    base = `${tiendaName}: $${precio}`;
  }
  if (cambio) {
    if (cambio.tipo === 'up') base += ` ↑ (antes: ${cambio.prev})`;
    else if (cambio.tipo === 'down') base += ` ↓ (antes: ${cambio.prev})`;
    else if (cambio.tipo === 'appeared') base += ' (nuevo)';
    else if (cambio.tipo === 'disappeared') base += ` (desapareció, era: ${cambio.prev})`;
  }
  return base;
};
</script>

<style scoped>
.heatmap-panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  overflow: hidden;
}
.heatmap-header {
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.heatmap-title {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.heatmap-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-faint);
  letter-spacing: 0.03em;
}
.table-container { overflow-x: auto; }

/* ============ TABLA ============ */
.heatmap-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-mono);
}
.heatmap-table thead th {
  text-align: left;
  padding: 7px 8px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  background: var(--color-surface-2);
  border-bottom: 1px solid var(--color-border-strong);
  white-space: nowrap;
}
.th-product { min-width: 260px; max-width: 320px; }
.th-comp { text-align: center; min-width: 68px; max-width: 90px; }

.th-sticky {
  position: sticky; left: 0; z-index: 3;
  background: var(--color-surface-2);
  box-shadow: 2px 0 4px rgba(0,0,0,0.08);
}
.td-sticky {
  position: sticky; left: 0; z-index: 2;
  background: var(--color-surface);
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}
.th-sticky-vip {
  position: sticky; left: 260px; z-index: 3;
  background: var(--color-surface-2);
  box-shadow: 2px 0 4px rgba(0,0,0,0.08);
}
.td-sticky-vip {
  position: sticky; left: 260px; z-index: 2;
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}

.heatmap-table tbody td {
  padding: 7px 8px;
  font-size: 11px;
  border-bottom: 1px solid var(--color-border);
  font-variant-numeric: tabular-nums;
}
.heatmap-table tbody tr:last-child td { border-bottom: none; }
.heatmap-table tbody tr:hover .td-product { background: var(--color-hover-row); }

/* ============ CELDAS ============ */
.td-product {
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: background 0.1s;
}
.product-code {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--color-text-faint);
  letter-spacing: 0.03em;
}
.product-desc {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--color-text);
  font-weight: 500;
  line-height: 1.3;
}

.td-price {
  text-align: center;
  font-weight: 700;
  font-size: 11.5px;
  background: var(--color-background);
  color: var(--color-text-muted);
  transition: transform 0.1s;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  position: relative;
}
.td-empty {
  color: var(--color-text-faint);
  font-weight: 400;
  opacity: 0.5;
}
.td-disappeared {
  color: #f59e0b !important;
  opacity: 1 !important;
  font-weight: 600 !important;
}

/* ============ INDICADORES DE CAMBIO ============ */
.change-arrow {
  position: absolute;
  top: 2px;
  right: 3px;
  font-size: 8px;
  font-weight: 900;
  line-height: 1;
  opacity: 0.95;
}

.cell-up    { border-top: 2px solid rgba(255, 100, 100, 0.85) !important; }
.cell-down  { border-top: 2px solid rgba(100, 220, 100, 0.85) !important; }
.cell-appeared   { outline: 2px dashed #22c55e; outline-offset: -2px; }
.cell-disappeared { border-top: 2px solid #f59e0b !important; }

/* ============ LEYENDA ============ */
.heatmap-legend {
  padding: 8px 16px;
  background: var(--color-surface-2);
  border-top: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.legend-label { font-weight: 700; color: var(--color-text-faint); letter-spacing: 0.1em; }
.legend-sep { width: 1px; height: 14px; background: var(--color-border-strong); margin: 0 4px; }
.legend-item { display: flex; align-items: center; gap: 6px; }
.legend-box {
  width: 14px; height: 10px; border-radius: 2px;
  border: 1px solid var(--color-border-strong);
}
.legend-box.legend-min { background: #059669; border-color: #059669; }
.legend-box.legend-mid { background: hsl(45, 65%, 42%); border-color: hsl(45, 65%, 42%); }
.legend-box.legend-max { background: #b91c1c; border-color: #b91c1c; }
.legend-box.legend-none { background: var(--color-background); }

.legend-change {
  font-size: 11px;
  font-weight: 900;
  width: 14px;
  text-align: center;
}
.legend-change.up         { color: rgba(255, 100, 100, 0.9); }
.legend-change.down       { color: rgba(100, 220, 100, 0.9); }
.legend-change.appeared   { color: #22c55e; }
.legend-change.disappeared { color: #f59e0b; }
</style>
